/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';
import { Checkbox, FormControlLabel, FormGroup, Grid } from '@mui/material';
import { CheckBoxComp, DatePickers, DialogForm, IDialogActions, ISnackbarAlert, SnackbarAlert, TextFieldInput, TextFieldReadOnly } from '../../../shared/components';
import { GenericService } from '../../../shared/services';
import { ListId, LocalStorange } from '../../../shared/environment';
import { AccessRoleDTO, AccessRoleFilterDTO, CompanyDTO, CompanyFilterDTO, UserDTO, UserFilterDTO } from '../../../shared/interfaces';
import { formatDateTime } from '../../../shared/utils';
import { useTranslation } from 'react-i18next';

interface IUserMaintenanceProps {
    data: any;
    setIsOpenMaintenance: (value: boolean) => void;
    isOpenMaintenance: boolean;
    setActionExecuted: (value: boolean) => void;
}

export const UserMaintenance: React.FC<IUserMaintenanceProps> = ({ data, isOpenMaintenance, setIsOpenMaintenance, setActionExecuted }) => {

    const [companyList, setCompanyList] = useState<CompanyDTO[]>([]);
    const [userList, setUserList] = useState<UserDTO[]>([]);
    const [userDTO, setUserDTO] = useState<UserDTO>();

    const [loading, setLoading] = useState(false);
    const [snackBarAlert, setSnackBarAlert] = useState<ISnackbarAlert>();
    const [isOpenMessage, setIsOpenMessage] = useState(false);

    const [accessRolePermissionDTO, setAccessRolePermissionDTO] = useState<AccessRoleDTO>();
    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');
    const { t } = useTranslation();

    useEffect(() => {
        setUserDTO({
            ...userDTO,
            id: '',
            userName: '',
            password: '',
            peopleId: '',
            isFirstAccess: true,
            isActive: true,
            // lastAccessDateTime: '',
            lastConnectedIp: '',
            accessDeadlineDateTime: new Date(new Date().setMonth(new Date().getMonth() + 3)),
            isAutomaticRenovation: true,
            companyId: userInfo.user.companyId,
        } as UserDTO);
        
        if(isOpenMaintenance){
            handleUserGetAllAsync(setUserList);
            handleGetUserByIdAsync();
            handleCompanyGetAll();
            handleGetPermission(ListId.authsphere.userId, setAccessRolePermissionDTO);
        }
    }, [data, isOpenMaintenance]);

    const handleGetPermission = useCallback(async (featureId: string, setList: any) => {
        const accessRoleFilterDTO: AccessRoleFilterDTO = {
            featureId: featureId,
            userId: userInfo.user.id,
        };
        GenericService.PostGenericAsync1('authsphere.AccessRole', 'GetPermissionAsync',accessRoleFilterDTO, setActionExecuted, setLoading, undefined,  setList,);
    }, []);
    
    const handleCompanyGetAll = useCallback(async () => {
        const companyFIlterDTO: CompanyFilterDTO = {
            name: '',
            businessRelationshipTypeId: '',
            statusId: '',
            orderBy: 'name ASC'
        };
        GenericService.GetAllAsync1('corpsphere.company', companyFIlterDTO, setLoading, setCompanyList, showMessage);
    }, []);
    
    const handleUserGetAllAsync = useCallback(async (setUserList: any) => {
        const userFilterDTO: UserFilterDTO = {
            companyId: '',
            peopleId: '',
            userName: '',
            isServiceAccount: false
        };
        GenericService.GetAllViewAsync1('authsphere.user', userFilterDTO, setActionExecuted, setLoading, setUserList, showMessage, false);

    }, []);

    const handleGetUserByIdAsync = useCallback(async () => {
        GenericService.GetByIdAsync1('authsphere.user', data.id, setLoading, setUserDTO, showMessage);
    }, [data]);

    const handleSaveClick = useCallback(async () => {
        const dto = userDTO as UserDTO;

        if (userDTO?.id === '' || userDTO?.id === undefined) {
            dto.createdById = userInfo.user.id;
            GenericService.AddAsync1('authsphere.user', dto, setUserDTO, setActionExecuted, setLoading, showMessage);
        }
        else {
            if(!accessRolePermissionDTO?.allowUpdate) {
                showMessage(t('message.allowUpdate'), 'info');
                return;
            }
            dto.updatedById = userInfo.user.id;
            GenericService.UpdateAsync1('authsphere.user', dto, setActionExecuted, setLoading, showMessage);
        }
    }, [userDTO]);

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarAlert(msg);
        setIsOpenMessage(true);
    }

    const actions: IDialogActions[] = [
        { id: 'save', text: t('user.maintenanceButton.save'), icon: 'save_outlined', iconColor: 'primary', setAction: handleSaveClick, isClose: isOpenMaintenance },
        { id: 'close', text: t('user.maintenanceButton.close'), icon: 'close_outlined', iconColor: 'secondary', setAction: () => void undefined },
    ];

    return (
        <React.Fragment>
            {isOpenMessage && (
                <SnackbarAlert
                    snackBarAlert={snackBarAlert}
                    setIsOpen={setIsOpenMessage}
                    isOpen={isOpenMessage}
                />
            )}
            <DialogForm
                title={t('user.detailsName')}
                icon='manage_accounts_outlined'
                setIsOpen={setIsOpenMaintenance}
                isOpen={isOpenMaintenance}
                fullWidth={true}
                maxWidth={'lg'}
                actions={actions}
                loading={loading}
            >
                <React.Fragment>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <TextFieldInput
                                id='userName'
                                label={t('user.maintenance.userName')}
                                value={userDTO?.userName}
                                setValue={setUserDTO}
                                obj={userDTO}
                                required={true}
                                maxLength={200}
                                autoFocus={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextFieldInput
                                id='peopleId'
                                label={t('user.maintenance.peopleId')}
                                value={userDTO?.peopleId}
                                setValue={setUserDTO}
                                obj={userDTO}
                                required={true}
                                maxLength={200}
                                readonly={userDTO?.id ? true : false}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <CheckBoxComp
                                id='isActive'
                                label={t('user.maintenance.isActive')}
                                setValue={setUserDTO}
                                objDTO={userDTO}
                                defaultChecked={userDTO?.isActive}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <CheckBoxComp
                                id='isFirstAccess'
                                label={t('user.maintenance.isFirstAccess')}
                                setValue={setUserDTO}
                                objDTO={userDTO}
                                defaultChecked={userDTO?.isFirstAccess}
                                disabled={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextFieldReadOnly
                                id={'accessDeadlineDateTime'}
                                label={t('user.maintenance.accessDeadlineDateTime')}
                                value={formatDateTime(String(userDTO?.lastAccessDateTime))}
                                hasDivider={false}
                                hasToolTip={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <CheckBoxComp
                                id='isAutomaticRenovation'
                                label={t('user.maintenance.isAutomaticRenovation')}
                                setValue={setUserDTO}
                                objDTO={userDTO}
                                defaultChecked={userDTO?.isAutomaticRenovation}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextFieldReadOnly
                                id={'lastAccessDateTime'}
                                label={t('user.maintenance.lastAccessDateTime')}
                                value={formatDateTime(String(userDTO?.lastAccessDateTime))}
                                hasDivider={false}
                                hasToolTip={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextFieldReadOnly
                                id={'lastConnectedIp'}
                                label={t('user.maintenance.lastConnectedIp')}
                                value={userDTO?.lastConnectedIp}
                                hasDivider={false}
                                hasToolTip={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextFieldReadOnly
                                id={'companyId'}
                                label={t('user.maintenance.company')}
                                value={companyList.find(x => x.id === userDTO?.companyId)?.name}
                                hasDivider={false}
                                hasToolTip={true}
                            />
                        </Grid>
                        {userDTO?.id ? (
                            <React.Fragment>
                                <Grid item xs={12} md={4}>
                                    <TextFieldReadOnly
                                        id={'id'}
                                        label='Id'
                                        value={userDTO.id}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'createdById'}
                                        label={t('user.maintenance.createdBy')}
                                        value={userList.find(x => x.id === userDTO.createdById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'creationDateTime'}
                                        label={t('user.maintenance.creationDateTime')}
                                        value={formatDateTime(String(userDTO.creationDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updatedById'}
                                        label={t('user.maintenance.updatedBy')}
                                        value={userList.find(x => x.id === userDTO.updatedById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updateDateTime'}
                                        label={t('user.maintenance.updateDateTime')}
                                        value={formatDateTime(String(userDTO.updateDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : undefined}
                    </Grid>
                </React.Fragment>
            </DialogForm>
        </React.Fragment>
    );
};